import { useEffect, useState, useContext, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import {
  UserPlus,
  Save,
  RotateCcw,
  ArrowLeft,
  Upload,
} from "lucide-react";

const EmployeeEntryForm = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const empid = queryParams.get("empid");
  const isEditMode = !!empid;

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchName = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const [signaturePreview, setSignaturePreview] = useState(null);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchName,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchName, userId]
  );

  const initialFormData = {
    empcode: "",
    empname: "",
    dep: "Inventory And Purchase",
    desig: "",
    branchid: branchId || "",
    userid: userId || "",
    empuserid: "",
    signature: null,
    filename: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [branchOptions, setBranchOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  const fetchBranches = async () => {
    try {
      const res = await axios.get("http://nissi-india.com/api/getBranchDropdown", {
        params: {
          branchid: branchId,
          branchtype: branchName,
        },
        headers: commonHeaders,
      });

      const branches = res.data?.branches || [];
      setBranchOptions(branches);
    } catch (err) {
      console.error("Failed to fetch branches:", err);
      toast.error("Failed to load branches");
      setBranchOptions([]);
    }
  };

const fetchEmployee = async (id) => {
  try {
    setLoading(true);

    const res = await axios.get(`http://nissi-india.com/api/getEmployee/${id}`, {
      headers: commonHeaders,
    });

    const emp = res.data?.data || {};

    setFormData({
      empcode: emp.empcode || "",
      empname: emp.empname || "",
      dep: emp.dep || "Inventory And Purchase",
      desig: emp.desig || "",
      branchid: String(emp.branchid || branchId || ""),
      userid: userId || "",
      empuserid: emp.empuserid || "",
      signature: null,
      filename: emp.signature || "",
    });

    // Set existing signature preview
    if (emp.signature) {
      setSignaturePreview(`http://nissi-india.com/files/${emp.signature}`);
    } else {
      setSignaturePreview(null);
    }
  } catch (err) {
    console.error("Failed to fetch employee:", err);
    toast.error("Failed to fetch employee data");
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchBranches();
  }, [commonHeaders]);

  useEffect(() => {
    if (isEditMode && empid) {
      fetchEmployee(empid);
    } else {
      setFormData({
        ...initialFormData,
        branchid: branchId || "",
        userid: userId || "",
      });
    }
  }, [empid, isEditMode, branchId, userId]);

const handleChange = (e) => {
  const { name, value, files } = e.target;

  if (name === "signature") {
    const file = files && files[0] ? files[0] : null;

    setFormData((prev) => ({
      ...prev,
      signature: file,
    }));

    if (file) {
      // Preview newly selected file
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignaturePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  } else {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }
};

const handleClearSignature = () => {
  setFormData((prev) => ({
    ...prev,
    signature: null,
  }));
  // Keep showing the original filename preview if in edit mode
  if (isEditMode && formData.filename) {
    setSignaturePreview(`http://nissi-india.com/files/${formData.filename}`);
  } else {
    setSignaturePreview(null);
  }
};

  const handleReset = () => {
    if (isEditMode && empid) {
      fetchEmployee(empid);
      return;
    }

    setFormData({
      ...initialFormData,
      branchid: branchId || "",
      userid: userId || "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.empcode ||
      !formData.empname ||
      !formData.dep ||
      !formData.desig ||
      !formData.branchid ||
      !formData.userid ||
      !formData.empuserid
    ) {
      toast.warning("Please fill all required fields");
      return;
    }

    if (!isEditMode && !formData.signature) {
      toast.warning("Please upload signature");
      return;
    }

    const payload = new FormData();
    payload.append("empcode", formData.empcode);
    payload.append("empname", formData.empname);
    payload.append("dep", formData.dep);
    payload.append("desig", formData.desig);
    payload.append("branchid", formData.branchid);
    payload.append("userid", formData.userid);
    payload.append("empuserid", formData.empuserid);

    if (formData.signature) {
      payload.append("signature", formData.signature);
    }

    if (isEditMode) {
      payload.append("filename", formData.filename || "");

      try {
        setLoading(true);
        const res = await axios.put(
          `http://nissi-india.com/api/updateEmployee/${empid}`,
          payload,
          {
            headers: {
              ...commonHeaders,
              "Content-Type": "multipart/form-data",
            },
          }
        );

              if (res.data?.success) {
  toast.success(res.data.message || "Employee updated successfully");
  setTimeout(() => navigate("/inventory/EmployeeDetail-Listing"), 800);
} else {
          toast.error(res.data?.message || "Update failed");
        }
      } catch (err) {
        console.error("Update failed:", err);
        toast.error(err.response?.data?.message || "Update failed");
      } finally {
        setLoading(false);
      }
    } else {
      try {
        setLoading(true);
        const res = await axios.post(
          "http://nissi-india.com/api/insertEmployee",
          payload,
          {
            headers: {
              ...commonHeaders,
              "Content-Type": "multipart/form-data",
            },
          }
        );

if (res.data?.success) {
  toast.success(res.data.message || "Employee added successfully");
  setTimeout(() => navigate("/inventory/EmployeeDetail-Listing"), 800);
} else {
          toast.error(res.data?.message || "Failed to add employee");
        }
      } catch (err) {
        console.error("Insert failed:", err);
        toast.error(err.response?.data?.message || "Failed to add employee");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <LayoutWrapper>
      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            {isEditMode ? "Edit Employee" : "Employee Entry"}
          </span>
        }
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Form Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden max-w-5xl mx-auto ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          {/* Form Header */}
          <div
            className={`px-6 py-4 border-b flex items-center justify-between ${
              isDark ? "border-slate-700/50 bg-slate-900/40" : "border-slate-200/50 bg-white/60"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <UserPlus className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className={`text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                  {isEditMode ? "Edit Employee Detail" : "Employee Detail Entry"}
                </h3>
                <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  {isEditMode
                    ? "Update employee information and signature"
                    : "Create a new employee record"}
                </p>
              </div>
            </div>

            <button
              onClick={() => navigate("/inventory/EmployeeDetail-Listing")}
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition ${
                isDark
                  ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                  : "border-slate-300 text-slate-700 hover:bg-slate-100"
              }`}
            >
              <ArrowLeft size={16} />
              Back
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
              {/* Left Column */}
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>
                    Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="dep"
                    value={formData.dep}
                    onChange={handleChange}
                    required
                    className={inputClass}
                  >
                    <option value="Inventory And Purchase">Inventory And Purchase</option>
                    <option value="MD Approval">MD Approval</option>
                    <option value="Purchase Request Approval">Purchase Request Approval</option>
                    <option value="Accounts">Accounts</option>
                    <option value="HR">HR</option>
                    <option value="HRe">HRe</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>
                    Employee Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="empname"
                    value={formData.empname}
                    onChange={handleChange}
                    required
                    className={inputClass}
                    placeholder="Enter employee name"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Branch <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="branchid"
                    value={formData.branchid}
                    onChange={handleChange}
                    required
                    className={inputClass}
                  >
                    <option value="">Select Branch</option>
                    {branchOptions.map((b, i) => {
                      const value = String(b.branchid || b.fld_Branchid || "");
                      const label = b.branchname || b.fld_BranchName || value;
                      return (
                        <option key={value || i} value={value}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
  <label className={labelClass}>
    Upload Signature {isEditMode ? "(Optional – replace existing)" : <span className="text-red-500">*</span>}
  </label>

  {/* Signature Preview */}
  {signaturePreview && (
    <div className="mb-3">
      <div
        className={`relative inline-block rounded-xl overflow-hidden border ${
          isDark ? "border-slate-600" : "border-slate-300"
        }`}
      >
        <img
          src={signaturePreview}
          alt="Signature Preview"
          className="max-h-32 max-w-full object-contain bg-white"
        />
      </div>
      <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
        {formData.signature
          ? `New file: ${formData.signature.name}`
          : formData.filename
          ? `Current file: ${formData.filename}`
          : ""}
      </p>
    </div>
  )}

  {/* Upload area */}
  <div
    className={`relative border-2 border-dashed rounded-xl p-4 transition ${
      isDark
        ? "border-slate-600 hover:border-slate-500"
        : "border-slate-300 hover:border-slate-400"
    }`}
  >
    <input
      type="file"
      name="signature"
      accept="image/*"
      onChange={handleChange}
      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
    />
    <div className="flex flex-col items-center gap-2 text-center">
      <Upload size={24} className={isDark ? "text-slate-400" : "text-slate-500"} />
      <span className={`text-sm ${isDark ? "text-slate-400" : "text-slate-600"}`}>
        {formData.signature
          ? formData.signature.name
          : isEditMode
          ? "Click to replace signature"
          : "Click or drag to upload signature"}
      </span>
    </div>
  </div>
</div>
              </div>

              {/* Right Column */}
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>
                    Employee Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="empcode"
                    value={formData.empcode}
                    onChange={handleChange}
                    required
                    className={inputClass}
                    placeholder="Enter employee code"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Designation <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="desig"
                    value={formData.desig}
                    onChange={handleChange}
                    required
                    className={inputClass}
                    placeholder="Enter designation"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    User Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="empuserid"
                    value={formData.empuserid}
                    onChange={handleChange}
                    required
                    className={inputClass}
                    placeholder="Enter user name"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-end gap-3 mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
              <button
                type="button"
                onClick={() => navigate("/inventory/EmployeeDetail-Listing")}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <ArrowLeft size={16} />
                Back
              </button>

              <button
                type="button"
                onClick={handleReset}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <RotateCcw size={16} />
                Reset
              </button>

              <button
                type="submit"
                disabled={loading}
                className={`inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
              >
                <Save size={16} />
                {loading
                  ? "Saving..."
                  : isEditMode
                  ? "Update Employee"
                  : "Save Employee"}
              </button>
            </div>
          </form>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default EmployeeEntryForm;